import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { ERROR_HANDLED } from './error-handler-interceptor';

@Injectable({
  providedIn: 'root',
})
export class DemographicService {
  STORAGE_KEY_STATES = 'demographic_storage_states';

  STORAGE_KEY_CITIES = 'demographic_storage_cities';

  static BASE_API = 'https://countriesnow.space/api/v0.1';

  public allCountries: Record<string, string> = {
    US: 'United States',
  };

  private countryStatesCache = signal<Map<string, Record<string, string>>>(new Map());

  private citiesCache = signal<Map<string, string[]>>(new Map());

  private readonly _http = inject(HttpClient);

  constructor() {
    const states = localStorage[this.STORAGE_KEY_STATES];
    if (states) {
      try {
        this.countryStatesCache.set(new Map(JSON.parse(states)));
      } catch {
        console.info('Skipping rebuild of demographic cache...');
      }
    }

    const cities = localStorage[this.STORAGE_KEY_CITIES];
    if (cities) {
      try {
        this.citiesCache.set(new Map(JSON.parse(cities)));
      } catch {
        console.info('Skipping rebuild of demographic cache...');
      }
    }
  }

  public getStates(country: string): Observable<Record<string, string>> {
    if (this.countryStatesCache().has(country)) {
      return of(this.countryStatesCache().get(country)!);
    }

    return this._http
      .post<{ data: { states: { name: string; state_code: string }[] } }>(
        `${DemographicService.BASE_API}/countries/states`,
        {
          country,
        },
        { context: new HttpContext().set(ERROR_HANDLED, true) },
      )
      .pipe(
        map((response) => {
          const states: Record<string, string> = {};
          response.data.states.forEach((state) => (states[state.state_code] = state.name));
          return states;
        }),
        tap((states) => this.countryStatesCache.update((cache) => cache.set(country, states))),
        tap(() => this.syncLocalStorage()),
        catchError(() => of({})),
      );
  }

  public getCitiesOfState(country: string, state: string): Observable<string[]> {
    if (this.citiesCache().has(`${country}_${state}`)) {
      return of(this.citiesCache().get(`${country}_${state}`)!);
    }

    return this._http
      .post<{ data: string[] }>(
        `${DemographicService.BASE_API}/countries/state/cities`,
        {
          country,
          state,
        },
        { context: new HttpContext().set(ERROR_HANDLED, true) },
      )
      .pipe(
        map((response) => response.data),
        tap((cities) =>
          this.citiesCache.update((cache) => cache.set(`${country}_${state}`, cities)),
        ),
      );
  }

  private syncLocalStorage() {
    localStorage.setItem(
      this.STORAGE_KEY_STATES,
      JSON.stringify(Array.from(this.countryStatesCache())),
    );
    localStorage.setItem(this.STORAGE_KEY_CITIES, JSON.stringify(Array.from(this.citiesCache())));
  }
}
